import { USER_ROLES, resolveUserRoleName } from "../constants";

/** True when the user is a Surveyor — forms and documents are view-only. */
export const isSurveyorUser = (user) => {
    if (!user) return false;
    const roleName = resolveUserRoleName(user);
    return roleName === USER_ROLES.SRVY || user.roleId === 12;
};

const isAdminUser = (user) =>
    user.roleId === 1 || user.role?.id === 1 ||
    (typeof user.role === 'string' && user.role.toLowerCase() === 'admin') ||
    (user.role?.name?.toLowerCase() === 'admin');

/** Admin or Office Assistant — office reviewer for Daily Log / general-form workflow. */
export const isOfficeReviewerUser = (user) => {
    if (!user) return false;
    if (isSurveyorUser(user)) return false;
    if (isAdminUser(user)) return true;
    const roleName = resolveUserRoleName(user);
    return roleName === USER_ROLES.OA;
};

/**
 * Daily Log: employees lock after Sent to Office or Completed.
 * Admin/OA can still review until Completed.
 */
export const isDailyLogReadOnly = (user, status) => {
    if (!user) return true;
    if (isSurveyorUser(user)) return true;
    if (isOfficeReviewerUser(user)) return status === "completed";
    return status === "send_to_office" || status === "completed";
};

/**
 * Checks if a form should be read-only based on user role, form status, and context.
 * 
 * @param {Object} user Current user object
 * @param {string} status Current status of the form (e.g., 'pending', 'send_to_office')
 * @param {string|number} targetUserId The ID of the user the form belongs to (if being viewed by admin)
 * @returns {boolean} True if the form should be read-only
 */
export const isFormReadOnly = (user, status, targetUserId) => {
    if (!user) return true;
    if (isSurveyorUser(user)) return true;

    const isAdmin = isAdminUser(user);

    const adminRestrictedStatuses = ['completed'];

    // Admin Case: 
    // Admins are read-only if they are NOT targeting a specific user's form (i.e., viewing their own profile forms or a generic view)
    if (isAdmin && adminRestrictedStatuses.includes(status)) {
        return true;
    } else if (isAdmin) {
        return !targetUserId;
    }

    // Non-Admin Case:
    // Regular users are read-only if the form has been submitted
    const restrictedStatuses = ['send_to_office', 'completed'];
    return restrictedStatuses.includes(status);
};

/**
 * Specifically for Evaluations: Admins can ALWAYS edit even if completed.
 */
export const isEvaluationReadOnly = (user, status, targetUserId) => {
    if (!user) return true;
    if (isSurveyorUser(user)) return true;

    const isAdmin = isAdminUser(user);

    if (isAdmin) {
        // Admins can always edit evaluations (as reviewers)
        return false;
    }

    // Non-admins are read-only once the evaluation is completed
    return status === 'completed';
};

/**
 * Checks if a user can perform a specific signature action (Employee vs Admin).
 * 
 * @param {Object} user Current user object
 * @param {string} signatureType 'applicant' or 'admin'
 * @returns {boolean} True if the user has permission to sign
 */
export const canUserSign = (user, signatureType) => {
    if (!user) return false;
    if (isSurveyorUser(user)) return false;

    const isAdmin = isAdminUser(user);

    if (signatureType === 'admin') return isAdmin;
    if (signatureType === 'applicant') return !isAdmin;

    return false;
};

/**
 * General Forms: admin has full access (fill, edit, employee sign, save/complete).
 * Employees may only edit their own non-submitted forms.
 */
export const isGeneralFormReadOnly = (user, status) => {
    if (!user) return true;
    if (isSurveyorUser(user)) return true;

    const isAdmin = isAdminUser(user);

    if (isAdmin) return false;

    const restrictedStatuses = ['send_to_office', 'completed'];
    return restrictedStatuses.includes(status);
};

/**
 * General Forms signatures: admin may sign employee (applicant) section;
 * employees may only sign as applicant.
 */
export const canUserSignGeneralForm = (user, signatureType) => {
    if (!user) return false;
    if (isSurveyorUser(user)) return false;

    const isAdmin = isAdminUser(user);

    if (signatureType === 'admin') return isAdmin;
    if (signatureType === 'applicant') return true; // employee or admin-on-behalf

    return false;
};

/**
 * List flags (Billed / Paid / Faxed / Signed) on General Forms — Admin & OA.
 */
export const canManageGeneralFormFlags = (user) => {
    if (!user) return false;
    if (isSurveyorUser(user)) return false;
    if (isAdminUser(user)) return true;
    const roleName = resolveUserRoleName(user);
    return roleName === USER_ROLES.OA;
};

